import React, { useState, useEffect, useMemo } from 'react';

import {
  HeadingBtnGroup,
  ClipReselectInfo,
} from '../../../../components/custom';
import { Modal } from '../../../../components/general';

import useAuthUserStore from '../../../../hooks/useAuthUserStore';
import { useVideoBuilderStore } from '../../../../stores/videoBuilder';

import {
  Person,
  HlReel,
  Shot,
  FocusedClip,
  WildcardFiller,
} from '../../../../types';
import { isEmpty, msecToSec } from '../../../../utils';

import ThumbnailsContainer from '../../components/thumbnailsContainer';
import HlReelPreview from './hlReelPreview';
import PersonClipsHeading from './personClipsHeading';

interface Props {
  openedPersonId: string | null;
  setOpenedPersonId: (id: string | null) => void;
  people: Person[];
  hlReel: HlReel | null;
  requiredShots: Shot[];
  noteText?: string | React.ReactNode;
}

const PersonClipsModal: React.FC<Props> = (props) => {
  const {
    openedPersonId,
    setOpenedPersonId,
    people,
    hlReel,
    requiredShots,
    noteText,
  } = props;

  const { adminMode } = useAuthUserStore();
  const { clipSelections, setClipSelections } = useVideoBuilderStore();

  const openedPerson = useMemo(
    () => people.find((person) => person.face.elementId === openedPersonId),
    [people, openedPersonId],
  );

  const [focusedClip, setFocusedClip] = useState<FocusedClip | null>(null);
  const [isCollapse, setIsCollapse] = useState<boolean | undefined>(undefined);
  const [isShowLowScoreGeneral, setIsShowLowScoreGeneral] = useState(false);
  const [isShowLowScore, setIsShowLowScore] = useState(false);

  const { clips, lowScoreClips } = useMemo(() => {
    const lowScoreClips = openedPerson?.lowScoreClips ?? [];
    const clips = (openedPerson?.clips ?? []).concat(
      isShowLowScore ? lowScoreClips : [],
    );

    return { clips, lowScoreClips };
  }, [openedPerson, isShowLowScore]);

  // if person has low-score clips selected, show all low-score clips by default
  useEffect(() => {
    const wildcardsFiller =
      clipSelections.find((selection) => selection.faceId === openedPersonId)
        ?.wildcardsFiller ?? [];

    const isLowScoreClipSelected = wildcardsFiller.some((selectedClip) =>
      openedPerson?.lowScoreClips?.find(
        (lowScoreClip) => lowScoreClip.elementId === selectedClip.clipId,
      ),
    );

    if (isLowScoreClipSelected) setIsShowLowScore(true);
    else setIsShowLowScore(isShowLowScoreGeneral); // so that other low-score score clips are still hidden (if not turned on)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openedPersonId]);

  // clear the focused clip when clicked outside of thumbnails container or highlight reel preview
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (!isEmpty(focusedClip) && !!openedPersonId) {
        const thumbnailContainerEl = document.getElementById(openedPersonId);
        const hlReelPreviewEl = document.getElementById(
          'hl-reel-preview-person',
        );

        if (
          !thumbnailContainerEl?.contains(e.target as Node) &&
          !hlReelPreviewEl?.contains(e.target as Node)
        )
          setFocusedClip(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [focusedClip, openedPersonId]);

  if (!openedPersonId || !openedPerson) return null;

  // toggle whether low-score clips are shown
  const toggleShowLowScoreClips = () => {
    const newIsShow = !isShowLowScore;

    // open full clips list, then reset collapse state
    if (newIsShow) {
      setIsCollapse(false);
      setTimeout(() => setIsCollapse(undefined), 15);
    }

    setIsShowLowScoreGeneral(newIsShow);
    setIsShowLowScore(newIsShow);
  };

  // change clip selection for particular wildcard
  const handleAssignFocusedClip = (shotIndex: number) => {
    if (!focusedClip) return;

    let clipSelectionsTemp = [...clipSelections];

    // find person selections data
    const foundSelections = clipSelectionsTemp.find(
      (s) => s.faceId === openedPersonId,
    );

    // set selection of focused clip, or remove if it is already selected
    foundSelections?.wildcardsFiller.forEach((w: WildcardFiller) => {
      if (w.clipId === focusedClip.clipId) w.clipId = null;
      else if (w.wildCardShotIndex === shotIndex) w.clipId = focusedClip.clipId;
    });

    setClipSelections(clipSelectionsTemp);
  };

  return (
    <Modal
      show={!isEmpty(openedPerson)}
      onHide={() => setOpenedPersonId(null)}
      customSize="sm:w-[calc((var(--thumbnail-width)_*_7)_+_10rem)] sm:max-w-[90vw]"
    >
      <Modal.Header className="flex-col-reverse sm:flex-row">
        <Modal.Title>
          <div className="flex gap-4 items-center">
            <img
              className="w-10 aspect-square rounded-full object-cover"
              src={openedPerson.img}
              alt={`${openedPerson.face.name} Icon`}
            />

            <h4>{openedPerson.face.name} – Personalised video preview</h4>
          </div>
        </Modal.Title>

        <div className="flex items-center gap-4">
          {adminMode && (
            <div className="italic">ID: {openedPerson.face.elementId}</div>
          )}

          <HeadingBtnGroup
            group={people}
            id={openedPersonId}
            setId={setOpenedPersonId}
          />
        </div>
      </Modal.Header>

      <Modal.Body>
        <ClipReselectInfo
          requiredShots={requiredShots}
          className="text-sm italic"
        />

        <HlReelPreview
          hlReel={hlReel}
          person={openedPerson}
          focusedClip={focusedClip}
          handleAssignFocusedClip={handleAssignFocusedClip}
        />

        <div>
          <PersonClipsHeading
            clips={clips}
            lowScoreClips={lowScoreClips}
            isShowLowScore={isShowLowScore}
            toggleShowLowScoreClips={toggleShowLowScoreClips}
          />

          <ThumbnailsContainer
            groupId={openedPersonId}
            videos={clips}
            collapseRequest={isCollapse}
            handleClick={(video) =>
              setFocusedClip({
                clipId: video.elementId,
                duration: msecToSec(video.segment?.durationMilliseconds),
              })
            }
            focusedClipId={focusedClip?.clipId}
          />
        </div>
      </Modal.Body>

      {noteText && (
        <Modal.Footer className="text-sm italic">{noteText}</Modal.Footer>
      )}
    </Modal>
  );
};

export default PersonClipsModal;
